/* istanbul ignore file */
/**
 * Created by rathompson on 11/3/21
 * File: Roles
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useContext, VFC } from 'react'
import PredefinedRolesTable from './table/PredefinedRolesTable'
import CustomRolesTable from './table/CustomRolesTable'
import {
  VIEW_ADD_CUSTOM_ROLE, VIEW_EDIT_CUSTOM_ROLE,
} from '../../../common/constants/GlobalConstants'
import { getVarValue, setCurrentViewing } from '../../../common/utils/Sparky'
import GlobalContext from '../../../common/context/GlobalContext'
import { setCustomRoleMetaData } from '../../../common/reducers/actions/IAMReducer.actions'
import { CUSTOM_ROLE_FORM_ACTIONS } from '../../constants/IAMConstants'
import IAMService from '../../services/IAMService'
import { useMessage } from '../../../common/providers/MessageProvider'
import { FF_CUSTOM_ROLES_ENABLED } from '../../../common/utils/FeatureFlags'
import StringTemplate from '../../../common/utils/StringTemplate'
import { LABEL_ROLE_DELETED } from '../../constants/LabelConstants'
import type { IAM_Role } from '../../../common/reducers/types/IAMReducer.types'

const customEnabled = getVarValue(FF_CUSTOM_ROLES_ENABLED)

/**
 *   Roles page
 */
const Roles: VFC<{
  getData: Function,
  canListRoles: boolean
}> = ({ getData, canListRoles }) => {

  const { treeReducer, dispatch } = useContext(GlobalContext)
  const { selectedTreeNode } = treeReducer
  const { createBanner, apiBannerHandler } = useMessage()

  const onAddClick = useCallback(() => {
    setCurrentViewing(VIEW_ADD_CUSTOM_ROLE)
    dispatch(setCustomRoleMetaData({ action: CUSTOM_ROLE_FORM_ACTIONS.ADD }))
  }, [dispatch])

  const onEditClick = useCallback((role: IAM_Role) => {
    setCurrentViewing(VIEW_EDIT_CUSTOM_ROLE)
    dispatch(setCustomRoleMetaData({ action: CUSTOM_ROLE_FORM_ACTIONS.EDIT, role }))
  }, [dispatch])

  const onCloneClick = useCallback((role: IAM_Role) => {
    setCurrentViewing(VIEW_ADD_CUSTOM_ROLE)
    dispatch(setCustomRoleMetaData({ action: CUSTOM_ROLE_FORM_ACTIONS.CLONE, role }))
  }, [dispatch])

  const onDeleteClick = useCallback((role: IAM_Role) => {
    IAMService.customRoles.delete(selectedTreeNode?.id, role.name).then(() => {
      const message = StringTemplate(LABEL_ROLE_DELETED, [role.name]) as string
      createBanner({ message, bannerProps: { appearance: 'success' } })
      getData(selectedTreeNode?.id, dispatch)
    }).catch(apiBannerHandler)
  }, [selectedTreeNode?.id, apiBannerHandler, createBanner, getData, dispatch])

  return (
    <div className='tw-relative tw-flex-1'>
      <div className='tw-absolute tw-inset-0 tw-p-3 tw-flex tw-flex-col tw-gap-4'>
        <PredefinedRolesTable/>
        {customEnabled && <CustomRolesTable {...{ onAddClick, onEditClick, onCloneClick, onDeleteClick, canListRoles }}/>}
      </div>
    </div>
  )
}

export default Roles
